import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, MessageCircle, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { mockData } from '../data/mock';
import { revealVariants, staggerContainer } from '../utils/motion';
import { useInViewOnce } from '../hooks/useInViewOnce';
import { MotionButton } from '../components/ui/motion-button';
import { contactAPI } from '../services/api';

export const Contact = () => {
  const [ref, isInView] = useInViewOnce();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await contactAPI.submitContactForm(formData);
      
      if (response.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello SAX! I'd like to get in touch about your services.");
    const whatsappUrl = `https://wa.me/${mockData.company.whatsapp.replace(/\s+/g, '').replace(/^\+/, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCallClick = () => {
    window.open(`tel:${mockData.company.phone}`, '_self');
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-slate-50" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              variants={revealVariants}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-8"
            >
              <span>Get in Touch</span>
            </motion.div>

            <motion.h1 
              variants={revealVariants}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Contact <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">SAX</span>
            </motion.h1>

            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Ready to discuss your project? Get in touch with our expert team. 
              We're here to help you achieve your goals with tailored consulting solutions.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <motion.div
              ref={ref}
              variants={staggerContainer}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              <motion.h2 
                variants={revealVariants}
                className="text-3xl font-bold text-gray-900 mb-8"
              >
                Let's Connect
              </motion.h2>

              <div className="space-y-8">
                <motion.div variants={revealVariants} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Office Address</h3>
                    <p className="text-gray-600 leading-relaxed">{mockData.company.location}</p>
                  </div>
                </motion.div>

                <motion.div variants={revealVariants} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                    <button
                      onClick={handleCallClick}
                      className="text-[#1F3C88] hover:text-[#FF5C8A] transition-colors font-medium"
                    >
                      {mockData.company.phone}
                    </button>
                  </div>
                </motion.div>

                <motion.div variants={revealVariants} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#1F3C88] to-[#FF8C42] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
                    <button
                      onClick={handleWhatsAppClick}
                      className="text-[#1F3C88] hover:text-[#FF5C8A] transition-colors font-medium"
                    >
                      {mockData.company.whatsapp}
                    </button>
                  </div>
                </motion.div>

                <motion.div variants={revealVariants} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#FF5C8A] to-[#1F3C88] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                    <a
                      href={`mailto:${mockData.company.email}`}
                      className="text-[#1F3C88] hover:text-[#FF5C8A] transition-colors font-medium"
                    >
                      {mockData.company.email}
                    </a>
                  </div>
                </motion.div>

                <motion.div variants={revealVariants} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#FF8C42] to-[#1F3C88] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Business Hours</h3>
                    <div className="text-gray-600 space-y-1">
                      <p>Sunday - Thursday: 9:00 AM - 6:00 PM</p>
                      <p>Saturday: 10:00 AM - 4:00 PM</p>
                      <p>Friday: Closed</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-8"
            >
              <motion.h2 
                variants={revealVariants}
                className="text-3xl font-bold text-gray-900 mb-8"
              >
                Send us a Message
              </motion.h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <motion.div variants={revealVariants}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                    placeholder="Your full name"
                  />
                </motion.div>

                <motion.div variants={revealVariants}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                    placeholder="your.email@example.com"
                  />
                </motion.div>

                <motion.div variants={revealVariants}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors"
                    placeholder="+971 XX XXX XXXX"
                  />
                </motion.div>

                <motion.div variants={revealVariants}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F3C88] focus:border-transparent transition-colors resize-none"
                    placeholder="Tell us about your project or inquiry..."
                  />
                </motion.div>

                <motion.div variants={revealVariants}>
                  <MotionButton
                    type="submit"
                    variant="secondary"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Send size={20} />
                      <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                    </div>
                  </MotionButton>
                </motion.div>

                {/* Success/Error Messages */}
                {submitStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-xl flex items-center space-x-2 ${
                      submitStatus === 'success' 
                        ? 'bg-green-50 text-green-800 border border-green-200' 
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                  >
                    {submitStatus === 'success' ? (
                      <>
                        <CheckCircle size={20} />
                        <span>Thank you for your message! We'll get back to you within 24 hours.</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={20} />
                        <span>Sorry, there was an error sending your message. Please try again or contact us directly.</span>
                      </>
                    )}
                  </motion.div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={revealVariants}
              className="text-3xl font-bold text-gray-900 mb-4"
            >
              Visit Our Office
            </motion.h2>
            <motion.p 
              variants={revealVariants}
              className="text-gray-600 max-w-2xl mx-auto"
            >
              Located in the heart of Dubai's business district, our office is easily accessible 
              and equipped with modern facilities for client meetings.
            </motion.p>
          </motion.div>

          <motion.div
            variants={revealVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="bg-white rounded-3xl p-4 shadow-lg"
          >
            {/* Google Maps Embed */}
            <div className="w-full h-96 bg-gray-200 rounded-2xl flex items-center justify-center relative overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3608.582!2d55.3014!3d25.2682!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f4348f0e0b000%3A0x0!2sDubai%20-%20Al%20Baraha%2C%20BIN%20FARES%202%2C%20Office%20101!5e0!3m2!1sen!2sae!4v1640000000000!5m2!1sen!2sae"
                width="100%"
                height="100%"
                style={{ border: 0, borderRadius: '1rem' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="SAX Office Location - Dubai Al Baraha"
              />
              
              {/* Fallback content */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#1F3C88]/10 to-[#FF5C8A]/10 flex items-center justify-center text-center p-8">
                <div>
                  <MapPin size={48} className="text-[#1F3C88] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Visit Our Office</h3>
                  <p className="text-gray-600">{mockData.company.location}</p>
                  <button
                    onClick={() => {
                      const mapsUrl = `https://maps.google.com/maps?q=${encodeURIComponent(mockData.company.location)}`;
                      window.open(mapsUrl, '_blank');
                    }}
                    className="mt-4 bg-[#1F3C88] text-white px-6 py-2 rounded-lg hover:bg-[#1a3373] transition-colors"
                  >
                    Open in Google Maps
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};